import klass from '../ProductCardView.module.scss';
import {
  ProductCardViewData,
  ProductCardOption,
  ProductCardSize,
  ProductCardTheme,
} from '@services/MarppleShop/features/ProductList/type';
import { ProductCardHelper } from '../ProductCardHelper';
import { font, staticFont } from '@services/MarppleShop/shared/typography';
import {
  ProductTagAlign,
  ProductTagBaseProps,
  ProductTagOption,
  ProductTagRadius,
  ProductTagSize,
  ProductTagVariant,
} from '@services/MarppleShop/renderApp/components/cells/ProductTag/ProductTagBase.ts';
import { ProductBadgeSize } from '@services/MarppleShop/features/ProductBadge';

export class ProductCardStyler {
  readonly theme: ProductCardTheme;

  constructor(
    private readonly size: ProductCardSize,
    private readonly data: ProductCardViewData,
    private readonly option: Omit<ProductCardOption, 'size'>,
  ) {
    // 기본 값은 전부 여기서 주입하기
    this.theme = option.theme ?? ProductCardTheme.light;
  }

  get klass() {
    return {
      theme: this.themeKlass,
      hidden: this.hiddenKlass,
      direction: this.directionKlass,
      soldOut: this.soldOutKlass,
      cardSize: this.cardSizeKlas,
    };
  }

  private get themeKlass(): string {
    const themeMap: Record<ProductCardTheme, string> = {
      light: klass.light,
      dark: klass.dark,
    };
    return themeMap[this.theme];
  }

  private get hiddenKlass(): string {
    return this.option.is_hidden ? klass.hidden : '';
  }

  private get directionKlass(): string {
    return this.option.is_horizontal ? klass.horizontal : klass.vertical;
  }

  private get cardSizeKlas(): string {
    const sizeMap: Record<ProductCardSize, string> = {
      xxSmall: klass.xxSmall,
      xSmall: klass.xSmall,
      small: klass.small,
      medium: klass.medium,
      large: klass.large,
    };
    return sizeMap[this.size];
  }

  private get soldOutKlass(): string {
    const fontSizeMap = new Map<ProductCardSize, string>([
      [ProductCardSize.xSmall, staticFont('bebas_16_bold')],
      [ProductCardSize.small, staticFont('bebas_24_bold')],
      [ProductCardSize.medium, staticFont('bebas_32_bold')],
      [ProductCardSize.large, staticFont('bebas_32_bold')],
    ]);

    const fontKlass = fontSizeMap.get(this.size) ?? '';

    return this.isSoldOutStyleVisible() ? `${klass.sold_out} ${fontKlass}` : '';
  }

  get reviewIcon(): string {
    const iconKlassMap = new Map<ProductCardSize, string[]>([
      [ProductCardSize.large, [klass.star_icon]],
      [ProductCardSize.medium, [klass.star_icon]],
      [ProductCardSize.small, [klass.star_icon]],
      [ProductCardSize.xSmall, [klass.star_icon]],
    ]);

    const klasses = iconKlassMap.get(this.size) ?? [];

    return klasses.join(' ');
  }

  get reviewWrap(): string {
    const klassMap = new Map<ProductCardSize, string[]>([
      [ProductCardSize.large, [klass.review_wrap, klass.large]],
      [ProductCardSize.medium, [klass.review_wrap, klass.medium]],
      [ProductCardSize.small, [klass.review_wrap, klass.small]],
      [ProductCardSize.xSmall, [klass.review_wrap, klass.xSmall]],
    ]);

    const klasses = klassMap.get(this.size) ?? [];

    return klasses.join(' ');
  }

  /**
   * 품절일 때 스타일을 적용할지 말지 컨트롤하는 함수
   * `this.isSoldOut`에서는 품절인지 아닌지 판단하는 로직만 넣기로 한다.
   */
  isSoldOutStyleVisible(): boolean {
    return !this.option.hide_badges && ProductCardHelper.isSoldOut(this.data);
  }

  /**
   * 랭킹 뱃지의 사이즈
   */
  get rankBadgeSize(): ProductBadgeSize {
    const sizeMap: Record<ProductCardSize, ProductBadgeSize> = {
      xxSmall: ProductBadgeSize.large,
      xSmall: ProductBadgeSize.large,
      small: ProductBadgeSize.large,
      medium: ProductBadgeSize.xLarge,
      large: ProductBadgeSize.xLarge,
    };

    return sizeMap[this.size];
  }

  get badgeSize(): ProductBadgeSize {
    const sizeMap: Record<ProductCardSize, ProductBadgeSize> = {
      xxSmall: ProductBadgeSize.small,
      xSmall: ProductBadgeSize.small,
      small: ProductBadgeSize.small,
      medium: ProductBadgeSize.medium,
      large: ProductBadgeSize.medium,
    };

    return sizeMap[this.size];
  }

  get bottomTagSize(): ProductTagSize {
    const sizeMap: Record<ProductCardSize, ProductTagSize> = {
      large: ProductTagSize.large,
      medium: ProductTagSize.large,
      small: ProductTagSize.medium,
      xSmall: ProductTagSize.medium,
      xxSmall: ProductTagSize.medium,
    };

    return sizeMap[this.size];
  }

  get watcherCountTagSize(): ProductTagSize {
    const sizeMap: Record<ProductCardSize, ProductTagSize> = {
      large: ProductTagSize.large,
      medium: ProductTagSize.large,
      small: ProductTagSize.small,
      xSmall: ProductTagSize.small,
      xxSmall: ProductTagSize.small,
    };

    return sizeMap[this.size];
  }

  get storeNameFont(): string {
    const fontMap: Record<ProductCardSize, string> = {
      [ProductCardSize.xxSmall]: font('12_bold'),
      [ProductCardSize.xSmall]: font('12_bold'),
      [ProductCardSize.small]: font('12_bold'),
      [ProductCardSize.medium]: font('14_bold'),
      [ProductCardSize.large]: font('14_bold'),
    };

    return fontMap[this.size];
  }

  get productNameFont(): string {
    const verticalFontMap: Record<ProductCardSize, string> = {
      [ProductCardSize.xxSmall]: font('12_medium'),
      [ProductCardSize.xSmall]: font('12_medium'),
      [ProductCardSize.small]: font('14_medium'),
      [ProductCardSize.medium]: font('16_medium'),
      [ProductCardSize.large]: font('16_medium'),
    };

    // 수평 타입은 당장은 사이즈 체계 없으나 생성해둠
    const horizontalFontMap: Record<ProductCardSize, string> = {
      [ProductCardSize.xxSmall]: font('14_medium'),
      [ProductCardSize.xSmall]: font('14_medium'),
      [ProductCardSize.small]: font('14_medium'),
      [ProductCardSize.medium]: font('14_medium'),
      [ProductCardSize.large]: font('14_medium'),
    };

    return this.option.is_horizontal ? horizontalFontMap[this.size] : verticalFontMap[this.size];
  }

  get priceFont(): string {
    const verticalFontMap: Record<ProductCardSize, string> = {
      xxSmall: staticFont('unica_14_bold'),
      xSmall: staticFont('unica_14_bold'),
      small: staticFont('unica_16_bold'),
      medium: staticFont('unica_20_bold'),
      large: staticFont('unica_20_bold'),
    };

    // 수평 타입은 당장은 사이즈 체계 없으나 생성해둠
    const horizontalFontMap: Record<ProductCardSize, string> = {
      xxSmall: staticFont('unica_16_bold'),
      xSmall: staticFont('unica_16_bold'),
      small: staticFont('unica_16_bold'),
      medium: staticFont('unica_16_bold'),
      large: staticFont('unica_16_bold'),
    };

    return this.option.is_horizontal ? horizontalFontMap[this.size] : verticalFontMap[this.size];
  }

  get reviewFontSize(): string {
    const fontMap: Record<ProductCardSize, string> = {
      [ProductCardSize.large]: staticFont('unica_14_regular'),
      [ProductCardSize.medium]: staticFont('unica_14_regular'),
      [ProductCardSize.small]: staticFont('unica_10_regular'),
      [ProductCardSize.xSmall]: staticFont('unica_10_regular'),
      [ProductCardSize.xxSmall]: staticFont('unica_10_regular'),
    };

    return fontMap[this.size];
  }

  get currencyFont(): string {
    const fontMap: Record<ProductCardSize, string> = {
      xxSmall: font('14_bold'),
      xSmall: font('14_bold'),
      small: font('16_bold'),
      medium: font('20_bold'),
      large: font('20_bold'),
    };
    return fontMap[this.size];
  }

  get realtimeTag(): Omit<ProductTagBaseProps, 'lang'> {
    const sizeMap: Record<ProductCardSize, Omit<ProductTagBaseProps, 'lang'>> = {
      xxSmall: {
        align: ProductTagAlign.start,
        options: [ProductTagOption.compact_label, ProductTagOption.fit_content],
        size: ProductTagSize.small,
        variant: ProductTagVariant.transparent,
        radius: ProductTagRadius.none,
      },
      xSmall: {
        align: ProductTagAlign.start,
        options: [ProductTagOption.compact_label, ProductTagOption.fit_content],
        size: ProductTagSize.small,
        variant: ProductTagVariant.transparent,
        radius: ProductTagRadius.none,
      },
      small: {
        align: ProductTagAlign.start,
        options: [ProductTagOption.fit_content],
        size: ProductTagSize.small,
        variant: ProductTagVariant.transparent,
        radius: ProductTagRadius.none,
      },
      medium: {
        align: ProductTagAlign.center,
        options: [ProductTagOption.stretch],
        size: ProductTagSize.large,
        variant: ProductTagVariant.filled,
        radius: ProductTagRadius.pill,
      },
      large: {
        align: ProductTagAlign.center,
        options: [ProductTagOption.stretch],
        size: ProductTagSize.large,
        variant: ProductTagVariant.filled,
        radius: ProductTagRadius.pill,
      },
    };

    return sizeMap[this.size];
  }

  get fontSize(): {
    store_name: string;
    product_name: string;
    price: string;
    currency: string;
    review: string;
  } {
    return {
      store_name: this.storeNameFont,
      product_name: this.productNameFont,
      price: this.priceFont,
      currency: this.currencyFont,
      review: this.reviewFontSize,
    };
  }

  get injectedStyles(): string {
    return this.option.injectedStyles ? this.option.injectedStyles.join('; ') : '';
  }
}
